/**
 * Client half of the encrypted location envelope (v1).
 *
 * Encrypts the GPS fix with AES-256-GCM and signs the envelope with
 * HMAC-SHA256 before it leaves the device, so the backend can reject
 * hand-rolled check-in requests that were not produced by the app.
 *
 * Server counterpart: utils/locationCrypto.js
 *
 * Keys come from the build environment:
 *   REACT_APP_ATTENDANCE_LOCATION_ENC_KEY
 *   REACT_APP_ATTENDANCE_LOCATION_SIGN_KEY
 * Both must match the server's ATTENDANCE_LOCATION_* values.
 */

const ENVELOPE_VERSION = 1;
const KEY_BYTES = 32;
const IV_BYTES = 12;
const NONCE_BYTES = 16;

export class LocationSealError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'LocationSealError';
    this.code = code;
  }
}

function getSubtle() {
  const subtle = window.crypto?.subtle;

  if (!subtle) {
    // Only happens outside a secure context (plain http on a non-localhost origin).
    throw new LocationSealError(
      'WEBCRYPTO_UNAVAILABLE',
      'Secure location encryption is unavailable on this connection.'
    );
  }

  return subtle;
}

function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

function base64ToBytes(value) {
  const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function parseKey(raw, label) {
  const trimmed = String(raw || '').trim();

  if (!trimmed) {
    throw new LocationSealError('LOCATION_CRYPTO_NOT_CONFIGURED', `${label} is not set in this build`);
  }

  const bytes = /^[0-9a-fA-F]{64}$/.test(trimmed) ? hexToBytes(trimmed) : base64ToBytes(trimmed);

  if (bytes.length !== KEY_BYTES) {
    throw new LocationSealError(
      'LOCATION_CRYPTO_NOT_CONFIGURED',
      `${label} must be ${KEY_BYTES} bytes (64 hex chars or base64)`
    );
  }

  return bytes;
}

function toBase64Url(bytes) {
  let binary = '';
  const view = new Uint8Array(bytes);
  for (let i = 0; i < view.length; i += 1) {
    binary += String.fromCharCode(view[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBytes(length) {
  const bytes = new Uint8Array(length);
  window.crypto.getRandomValues(bytes);
  return bytes;
}

// Imported keys are cached: importKey on every check-in is wasted work.
let cachedKeys = null;

async function getKeys() {
  if (cachedKeys) {
    return cachedKeys;
  }

  const subtle = getSubtle();

  const encryptionKeyBytes = parseKey(
    process.env.REACT_APP_ATTENDANCE_LOCATION_ENC_KEY,
    'REACT_APP_ATTENDANCE_LOCATION_ENC_KEY'
  );
  const signingKeyBytes = parseKey(
    process.env.REACT_APP_ATTENDANCE_LOCATION_SIGN_KEY,
    'REACT_APP_ATTENDANCE_LOCATION_SIGN_KEY'
  );

  const [encryptionKey, signingKey] = await Promise.all([
    subtle.importKey('raw', encryptionKeyBytes, { name: 'AES-GCM' }, false, ['encrypt']),
    subtle.importKey('raw', signingKeyBytes, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  ]);

  cachedKeys = { encryptionKey, signingKey };
  return cachedKeys;
}

/**
 * True when this build has location encryption keys configured.
 * Lets the UI fall back to the plaintext request shape instead of hard-failing
 * on a build that was made before the keys were added.
 */
export function isLocationEncryptionConfigured() {
  try {
    parseKey(process.env.REACT_APP_ATTENDANCE_LOCATION_ENC_KEY, 'enc');
    parseKey(process.env.REACT_APP_ATTENDANCE_LOCATION_SIGN_KEY, 'sign');
    return Boolean(window.crypto?.subtle);
  } catch (error) {
    return false;
  }
}

/**
 * Seal a GPS fix into an encrypted, signed envelope.
 *
 * @param {object} args
 * @param {'check_in'|'check_out'} args.action  Bound into the ciphertext so a
 *   captured check-in envelope cannot be replayed as a check-out.
 * @param {number|string} args.userId  Bound into the ciphertext so an envelope
 *   cannot be shared between employees.
 * @param {object} args.position  Output of the geolocation call.
 * @returns {Promise<object>} envelope to send as `locationPayload`
 */
export async function sealLocationPayload({ action, userId, position, isMock = false }) {
  const subtle = getSubtle();
  const { encryptionKey, signingKey } = await getKeys();

  const payload = {
    latitude: position.latitude,
    longitude: position.longitude,
    accuracy: position.accuracy ?? null,
    altitude: position.altitude ?? null,
    speed: position.speed ?? null,
    heading: position.heading ?? null,
    timestamp: position.timestamp ? new Date(position.timestamp).toISOString() : new Date().toISOString(),
    isMock,
    action,
    userId
  };

  const iv = randomBytes(IV_BYTES);
  const nonce = randomBytes(NONCE_BYTES);

  // WebCrypto appends the GCM auth tag to the ciphertext, matching what the
  // server splits off in decryptPayload().
  const ciphertext = await subtle.encrypt(
    { name: 'AES-GCM', iv },
    encryptionKey,
    new TextEncoder().encode(JSON.stringify(payload))
  );

  const envelope = {
    v: ENVELOPE_VERSION,
    iv: toBase64Url(iv),
    data: toBase64Url(ciphertext),
    ts: Date.now(),
    nonce: toBase64Url(nonce)
  };

  const signingInput = [envelope.v, envelope.iv, envelope.data, envelope.ts, envelope.nonce].join('.');
  const signature = await subtle.sign('HMAC', signingKey, new TextEncoder().encode(signingInput));

  envelope.sig = toBase64Url(signature);

  return envelope;
}

/**
 * Prove to the server that a device enrollment came from the real app rather
 * than a hand-rolled request, using the same signing key as the location
 * envelope. Recorded server-side as `attested`.
 *
 * Bound to the employee's email, which the app knows at login time.
 * Returns null on builds without keys configured, which the server tolerates.
 */
export async function signDeviceAttestation({ deviceId, email }) {
  const subject = String(email || '').trim().toLowerCase();

  if (!deviceId || !subject || !isLocationEncryptionConfigured()) {
    return null;
  }

  try {
    const subtle = getSubtle();
    const { signingKey } = await getKeys();
    const ts = Date.now();

    const signature = await subtle.sign(
      'HMAC',
      signingKey,
      new TextEncoder().encode(`${deviceId}.${subject}.${ts}`)
    );

    return { ts, sig: toBase64Url(signature) };
  } catch (error) {
    // Enrollment still works unattested; never block login on this.
    console.warn('Device attestation unavailable:', error?.message);
    return null;
  }
}
